import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ModuleNav, { MODULE_NAVS } from '../../components/ModuleNav';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Dropdown,
  Empty,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import {
  SmileOutlined,
  ClearOutlined,
  CopyOutlined,
  DownloadOutlined,
  EyeOutlined,
  UndoOutlined,
  FilterOutlined,
  ManOutlined,
  PieChartOutlined,
  ReloadOutlined,
  SettingOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  WomanOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import lib from '../../assets/utils/lib';
import { usePermissions } from '../../assets/utils/usePermissions';
import DebounceSelect from '../compoinents/DebouncedSelect';
import { EnroleeBiodataModal } from '../compoinents/enroleeData';
import DashboardHero from '../dashboard/components/DashboardHero';
import DashboardSection from '../dashboard/components/DashboardSection';
import { RowListSkeleton, TableSkeleton } from '../dashboard/components/DashboardSkeleton';
import { hasMetricValue } from '../dashboard/utils';
import '../dashboard/index.css';
import './enrollee-dashboard.css';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const ENROLLEE_STATUS_OPTIONS = [
  { label: 'Active', value: 1 },
  { label: 'Inactive', value: 0 },
  { label: 'Pending', value: 2 },
];
const BOOL_OPTIONS = [
  { label: 'Yes', value: 1 },
  { label: 'No', value: 0 },
];
const GENDER_OPTIONS = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
];

const GROUP_KEYS = [
  { title: 'Gender', key: 'by_gender' },
  { title: 'Age band', key: 'by_age_band' },
  { title: 'State', key: 'by_enrollee_state' },
  { title: 'LGA', key: 'by_lga' },
  { title: 'Role', key: 'by_role' },
  { title: 'Relationship', key: 'by_relationship' },
  { title: 'Premium type', key: 'by_premium_type' },
  { title: 'Plan name', key: 'by_plan_name' },
  { title: 'Plan type', key: 'by_plan_type_name' },
];

const REGISTRY_COLUMN_META = {
  name: { label: 'Name', defaultVisible: true },
  insurance_no: { label: 'Insurance no', defaultVisible: true },
  status: { label: 'Status', defaultVisible: true },
  plan_name: { label: 'Plan', defaultVisible: true },
  organization: { label: 'Organization', defaultVisible: true },
  gender: { label: 'Gender', defaultVisible: true },
  age: { label: 'Age', defaultVisible: true },
  policy_no: { label: 'Policy no', defaultVisible: false },
  relationship: { label: 'Relationship', defaultVisible: false },
  role: { label: 'Role', defaultVisible: false },
  state_lga: { label: 'State / LGA', defaultVisible: false },
  home_address: { label: 'Home address', defaultVisible: false },
  policy_holder_name: { label: 'Policy holder', defaultVisible: false },
  premium_type: { label: 'Premium type', defaultVisible: false },
  policy_cost: { label: 'Policy cost', defaultVisible: false },
  expiry_date: { label: 'Policy expiry', defaultVisible: false },
  coverage_start_date: { label: 'Coverage start', defaultVisible: false },
};

const DEFAULT_VISIBLE_COLUMN_KEYS = Object.keys(REGISTRY_COLUMN_META).filter(
  (key) => REGISTRY_COLUMN_META[key].defaultVisible
);

const FILTER_META = {
  search: { label: 'Search', format: (value) => value },
  gender: { label: 'Gender', format: (value) => normalizeLabel(value) },
  enrollee_status: {
    label: 'Enrollee status',
    format: (value) => (Number(value) === 1 ? 'Active' : Number(value) === 0 ? 'Inactive' : 'Pending'),
  },
  update_required: { label: 'Pending update', format: (value) => (Number(value) === 1 ? 'Yes' : 'No') },
  flagged: { label: 'Flagged', format: (value) => (Number(value) === 1 ? 'Yes' : 'No') },
  age_min: { label: 'Minimum age', format: (value) => String(value) },
  age_max: { label: 'Maximum age', format: (value) => String(value) },
  dob_from: { label: 'DOB from', format: (value) => String(value) },
  dob_to: { label: 'DOB to', format: (value) => String(value) },
  org_id: { label: 'Organization ID', format: (value) => String(value) },
  social_no: { label: 'Organization social no', format: (value) => String(value) },
};

const normalizeLabel = (value) => {
  if (value == null || value === '' || value === '(empty)') return 'Unknown';
  return String(value);
};

const getEnrolleeName = (row) => {
  const enrollee = row?.enrollee || {};
  return [enrollee.first_name, enrollee.middle_name, enrollee.surname].filter(Boolean).join(' ');
};

const renderEnrolleeStatus = (row) => {
  const enrollee = row?.enrollee || {};
  return (
    <Space wrap size={4}>
      {Number(enrollee?.status) !== 1 && <Tag color="red">Inactive</Tag>}
      {Number(enrollee?.update_required) === 1 && <Tag color="gold">Pending update</Tag>}
      {Number(enrollee?.flagged) === 1 && <Tag color="volcano">Flagged</Tag>}
      {Number(enrollee?.status) === 1
        && Number(enrollee?.update_required) !== 1
        && Number(enrollee?.flagged) !== 1 && (
        <Tag color="green">Active</Tag>
      )}
    </Space>
  );
};

const copyInsuranceNo = (insuranceNo, event) => {
  event?.stopPropagation();
  if (!insuranceNo) return;
  navigator.clipboard?.writeText(String(insuranceNo)).then(() => {
    notification.success({ message: 'Insurance number copied' });
  }).catch(() => {
    notification.error({ message: 'Could not copy to clipboard' });
  });
};

const findGroupCount = (collection, matcher) => {
  if (!Array.isArray(collection)) return 0;
  const row = collection.find((entry) => matcher(String(entry?.key || '').toLowerCase()));
  return Number(row?.count || 0);
};

const findUnderFiveCount = (summaryData) => {
  const byCfoSplit = summaryData?.by_age_under5_over5;
  if (byCfoSplit && typeof byCfoSplit === 'object' && !Array.isArray(byCfoSplit)) {
    return Number(byCfoSplit?.under_5 || 0);
  }
  const cfoCount = findGroupCount(byCfoSplit, (key) => key === 'under_5');
  if (cfoCount > 0 || Array.isArray(byCfoSplit)) return cfoCount;
  return findGroupCount(summaryData?.by_age_band, (key) => key.includes('under') || key.includes('<5') || key === '0-4');
};

const findFiveAndAboveCount = (summaryData) => {
  const byCfoSplit = summaryData?.by_age_under5_over5;
  if (byCfoSplit && typeof byCfoSplit === 'object' && !Array.isArray(byCfoSplit)) {
    return Number(byCfoSplit?.five_and_above || 0);
  }
  const cfoCount = findGroupCount(byCfoSplit, (key) => key === 'five_and_above' || key === '5+');
  if (cfoCount > 0 || Array.isArray(byCfoSplit)) return cfoCount;
  return findGroupCount(
    summaryData?.by_age_band,
    (key) => key.includes('over') || key.includes('5+') || key.includes('above') || key.includes('>=5')
  );
};

const ARCHIVED_OPTIONS = [
  { label: 'Hide archived', value: '0' },
  { label: 'Archived only', value: '1' },
  { label: 'All', value: 'all' },
];

const pickNonEmpty = (payload) => {
  const out = {};
  Object.keys(payload).forEach((key) => {
    const value = payload[key];
    if (value === '' || value == null) return;
    out[key] = value;
  });
  return out;
};

const parseNumeric = (value) => {
  if (value == null || value === '') return '';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : '';
};

const BreakdownPanel = ({ title, data = [], loading = false }) => {
  const normalized = Array.isArray(data) ? data : [];

  if (loading) {
    return (
      <div className="en-breakdown-panel">
        <h4 className="en-breakdown-panel__title">{title}</h4>
        <RowListSkeleton rows={4} />
      </div>
    );
  }

  if (!normalized.length) {
    return (
      <div className="en-breakdown-panel">
        <h4 className="en-breakdown-panel__title">{title}</h4>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
      </div>
    );
  }

  const maxCount = Math.max(...normalized.map((row) => Number(row?.count || 0)), 1);

  return (
    <div className="en-breakdown-panel">
      <h4 className="en-breakdown-panel__title">{title}</h4>
      {normalized.slice(0, 8).map((row, idx) => {
        const count = Number(row?.count || 0);
        const pct = Math.round((count / maxCount) * 100);
        return (
          <div key={`${title}-${idx}`} className="en-bar-row">
            <div className="en-bar-row__head">
              <span>{normalizeLabel(row?.key)}</span>
              <strong>{count.toLocaleString()}</strong>
            </div>
            <div className="en-bar-row__track">
              <div className="en-bar-row__fill" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const EnrolleeDashboard = () => {
  const { hasAnyPermissionWithLegacy } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  // Gates the "Organization (admin)" filter section and whether org_id / social_no may be
  // sent on the outbound filter payload — a cross-org filtering capability, not a
  // record-set gate (the backend scopes the list independently).
  // NOTE: enrollees.read_all is in the app catalog but seeded to no role today; admin.* is
  // what actually carries the legacy super user (1) across on a seeded map.
  const canFilterAcrossOrgs = hasAnyPermissionWithLegacy(
    ['admin.*', 'corporations.read_all', 'enrollees.read_all'],
    [1],
  );

  const [filters, setFilters] = useState(() => ({
    search: searchParams.get('search') || '',
    gender: searchParams.get('gender') || '',
    enrollee_status: parseNumeric(searchParams.get('enrollee_status')),
    update_required: parseNumeric(searchParams.get('update_required')),
    flagged: parseNumeric(searchParams.get('flagged')),
    social_no: searchParams.get('social_no') || '',
    org_id: parseNumeric(searchParams.get('org_id')),
    age_min: parseNumeric(searchParams.get('age_min')),
    age_max: parseNumeric(searchParams.get('age_max')),
    dob_from: searchParams.get('dob_from') || '',
    dob_to: searchParams.get('dob_to') || '',
    archived: searchParams.get('archived') || '0',
  }));
  const [pagination, setPagination] = useState(() => ({
    page: parseNumeric(searchParams.get('page')) || 1,
    limit: parseNumeric(searchParams.get('limit')) || 20,
  }));

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search || '');
  const [summaryData, setSummaryData] = useState({});
  const [listData, setListData] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [listError, setListError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [selectedOrgLabel, setSelectedOrgLabel] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(DEFAULT_VISIBLE_COLUMN_KEYS);
  const [tableSize, setTableSize] = useState('small');
  const [biodataRow, setBiodataRow] = useState(null);
  const [showBiodata, setShowBiodata] = useState(false);
  const [unarchivingNo, setUnarchivingNo] = useState(null);

  // Drives viewOnly on the enrollee biodata modal. clients.update keeps legacy
  // Customer Care (2); enrollees.manage_all is catalogued but seeded to no role today.
  const canEditEnrollee = hasAnyPermissionWithLegacy(
    ['enrollees.manage_all', 'clients.update', 'clients.manage_all', 'admin.*'],
    [1, 2],
  );

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleOrgSearch = useCallback(async (search) => {
    const resp = await lib.orgs({ mode: 'mini', search });
    const body = resp?.data;
    if (body?.error || !Array.isArray(body?.data)) return [];
    return body.data.map((org) => ({
      label: org?.name || `${org?.social_no || ''}`.trim() || `Org ${org?.id}`,
      value: Number(org?.id),
      social_no: org?.social_no,
    }));
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(filters.search || '');
    }, 400);
    return () => clearTimeout(id);
  }, [filters.search]);

  const apiFilters = useMemo(() => {
    const payload = {
      ...filters,
      search: debouncedSearch,
    };
    if (!canFilterAcrossOrgs) {
      delete payload.org_id;
      delete payload.social_no;
    }
    return pickNonEmpty(payload);
  }, [debouncedSearch, filters, canFilterAcrossOrgs]);

  const nonPagingSignature = useMemo(() => JSON.stringify(apiFilters), [apiFilters]);

  useEffect(() => {
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [nonPagingSignature]);

  useEffect(() => {
    const query = pickNonEmpty({
      ...apiFilters,
      page: pagination.page,
      limit: pagination.limit,
    });
    setSearchParams(query, { replace: true });
  }, [apiFilters, pagination.limit, pagination.page, setSearchParams]);

  const loadDashboardData = useCallback(async () => {
    setSummaryLoading(true);
    setListLoading(true);
    const [summaryResult, listResult] = await Promise.allSettled([
      lib.getAdminEnrolleesSummary(apiFilters),
      lib.getAdminEnrolleesList({ ...apiFilters, page: pagination.page, limit: pagination.limit }),
    ]);

    if (summaryResult.status === 'fulfilled') {
      const body = summaryResult.value?.data;
      if (body && !Number(body?.error)) {
        setSummaryData(body?.data || {});
        setSummaryError('');
      } else {
        setSummaryError(body?.message || 'Could not load enrollee summary.');
      }
    } else {
      setSummaryError('Could not load enrollee summary. Please try again.');
    }
    setSummaryLoading(false);

    if (listResult.status === 'fulfilled') {
      const body = listResult.value?.data;
      if (body && !Number(body?.error)) {
        const listPayload = body?.data;
        const items = Array.isArray(listPayload?.items)
          ? listPayload.items
          : Array.isArray(listPayload)
            ? listPayload
            : [];
        const pageMeta = listPayload?.pagination || body?.pagination || {};
        setListData(items);
        setPagination((prev) => ({
          ...prev,
          page: Number(pageMeta?.page || prev.page),
          limit: Number(pageMeta?.limit || prev.limit),
          total: Number(pageMeta?.total || 0),
          pages: Number(pageMeta?.pages || 1),
        }));
        setListError('');
      } else {
        setListError(body?.message || 'Could not load enrollee list.');
      }
    } else {
      setListError('Could not load enrollee list. Please try again.');
    }
    setListLoading(false);
    setLastRefresh(dayjs());
  }, [apiFilters, pagination.limit, pagination.page]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const clearFilters = () => {
    setSelectedOrgLabel('');
    setFilters({
      search: '',
      gender: '',
      enrollee_status: '',
      update_required: '',
      flagged: '',
      social_no: '',
      org_id: '',
      age_min: '',
      age_max: '',
      dob_from: '',
      dob_to: '',
    });
  };

  const onExport = async () => {
    try {
      setExporting(true);
      await lib.exportAdminEnrollees({ ...apiFilters, max_rows: 10000 });
      notification.success({ message: 'Export started successfully.' });
    } catch (e) {
      notification.error({ message: e?.message || 'Export failed. Please retry.' });
    } finally {
      setExporting(false);
    }
  };

  const openBiodata = useCallback((row) => {
    setBiodataRow(row);
    setShowBiodata(true);
  }, []);

  const closeBiodata = useCallback(() => {
    setShowBiodata(false);
    setBiodataRow(null);
  }, []);

  const allRegistryColumns = useMemo(() => ({
    name: {
      title: 'Name',
      key: 'name',
      fixed: 'left',
      width: 180,
      ellipsis: true,
      render: (_, row) => {
        const name = getEnrolleeName(row);
        return (
          <Tooltip title={name}>
            <Text strong ellipsis style={{ maxWidth: 168 }}>
              {name || '—'}
            </Text>
          </Tooltip>
        );
      },
    },
    insurance_no: {
      title: 'Insurance no',
      key: 'insurance_no',
      width: 130,
      ellipsis: true,
      render: (_, row) => {
        const id = row?.enrollee?.insurance_no;
        if (!id) return '—';
        return (
          <Tooltip title={id}>
            <Text code ellipsis style={{ maxWidth: 118 }}>
              {id}
            </Text>
          </Tooltip>
        );
      },
    },
    status: {
      title: 'Status',
      key: 'status',
      width: 140,
      render: (_, row) => renderEnrolleeStatus(row),
    },
    plan_name: {
      title: 'Plan',
      key: 'plan_name',
      width: 140,
      ellipsis: true,
      render: (_, row) => {
        const v = normalizeLabel(row?.plan?.plan_name);
        return (
          <Tooltip title={v}>
            <Text ellipsis style={{ maxWidth: 128 }}>{v}</Text>
          </Tooltip>
        );
      },
    },
    organization: {
      title: 'Organization',
      key: 'organization',
      width: 160,
      ellipsis: true,
      render: (_, row) => {
        const v = normalizeLabel(row?.organization?.org_name);
        return (
          <Tooltip title={v}>
            <Text ellipsis style={{ maxWidth: 148 }}>{v}</Text>
          </Tooltip>
        );
      },
    },
    gender: {
      title: 'Gender',
      key: 'gender',
      width: 88,
      render: (_, row) => normalizeLabel(row?.enrollee?.gender),
    },
    age: {
      title: 'Age',
      key: 'age',
      width: 64,
      align: 'right',
      render: (_, row) => row?.enrollee?.age ?? '—',
    },
    policy_no: {
      title: 'Policy no',
      key: 'policy_no',
      width: 120,
      ellipsis: true,
      render: (_, row) => normalizeLabel(row?.enrollee?.policy_no),
    },
    relationship: {
      title: 'Relationship',
      key: 'relationship',
      width: 120,
      render: (_, row) => normalizeLabel(row?.enrollee?.relationship),
    },
    role: {
      title: 'Role',
      key: 'role',
      width: 100,
      render: (_, row) => normalizeLabel(row?.enrollee?.role),
    },
    state_lga: {
      title: 'State / LGA',
      key: 'state_lga',
      width: 150,
      ellipsis: true,
      render: (_, row) => {
        const v = `${normalizeLabel(row?.enrollee?.state)} / ${normalizeLabel(row?.enrollee?.lga)}`;
        return (
          <Tooltip title={v}>
            <Text ellipsis style={{ maxWidth: 138 }}>{v}</Text>
          </Tooltip>
        );
      },
    },
    home_address: {
      title: 'Home address',
      key: 'home_address',
      width: 180,
      ellipsis: true,
      render: (_, row) => {
        const v = row?.enrollee?.contact_address ? normalizeLabel(row?.enrollee?.contact_address) : '—';
        return (
          <Tooltip title={v}>
            <Text ellipsis style={{ maxWidth: 168 }}>{v}</Text>
          </Tooltip>
        );
      },
    },
    policy_holder_name: {
      title: 'Policy holder',
      key: 'policy_holder_name',
      width: 140,
      ellipsis: true,
      render: (_, row) => normalizeLabel(row?.policy?.policy_holder_name),
    },
    premium_type: {
      title: 'Premium type',
      key: 'premium_type',
      width: 120,
      render: (_, row) => normalizeLabel(row?.policy?.premium_type),
    },
    policy_cost: {
      title: 'Policy cost',
      key: 'policy_cost',
      width: 110,
      align: 'right',
      render: (_, row) => {
        const cost = Number(row?.policy?.policy_cost);
        return cost ? `₦${cost.toLocaleString()}` : '—';
      },
    },
    expiry_date: {
      title: 'Policy expiry',
      key: 'expiry_date',
      width: 120,
      render: (_, row) => normalizeLabel(row?.policy?.expiry_date),
    },
    coverage_start_date: {
      title: 'Coverage start',
      key: 'coverage_start_date',
      width: 120,
      render: (_, row) => (
        row?.policy?.coverage_start_date ? normalizeLabel(row?.policy?.coverage_start_date) : '—'
      ),
    },
  }), []);

  /**
   * Unarchive restores the row with status = 0 — it never reactivates care, so
   * the operator still has to activate explicitly afterwards. Say that, or the
   * enrollee looks restored-but-broken.
   */
  const handleUnarchive = useCallback(async (insuranceNo) => {
    if (!insuranceNo) return;
    setUnarchivingNo(insuranceNo);
    const res = await lib.unarchiveEnrollee(insuranceNo, {});
    setUnarchivingNo(null);

    if (res?.ok) {
      notification.success({
        message: res?.message || 'Enrollee unarchived',
        description: 'They are restored as inactive — activate them to resume care.',
      });
      loadDashboardData();
      return;
    }
    if (res?.code === lib.ENROLLEE_ARCHIVE_CODES?.NO_SLOT) {
      const remaining = Number(res?.data?.slots?.total_remaining ?? 0);
      notification.warning({
        message: 'No enrollment slot available on this policy',
        description: `Their seat was released when they were archived and has since been filled. ${remaining} slots remain — free one, or raise the plan size, then try again.`,
      });
      return;
    }
    if (res?.code === lib.ENROLLEE_ARCHIVE_CODES?.UNAVAILABLE) {
      notification.warning({
        message: 'Archiving is temporarily unavailable',
        description: 'The archive feature is still being rolled out. Nothing was changed — please try again shortly.',
      });
      return;
    }
    notification.error({ message: res?.message || 'Failed to unarchive enrollee' });
  }, [loadDashboardData]);

  const registryColumns = useMemo(() => {
    const dataCols = Object.keys(REGISTRY_COLUMN_META)
      .filter((key) => visibleColumnKeys.includes(key) && allRegistryColumns[key])
      .map((key) => allRegistryColumns[key]);

    return [
      ...dataCols,
      {
        title: '',
        key: 'actions',
        fixed: 'right',
        width: 72,
        render: (_, row) => {
          const insuranceNo = row?.enrollee?.insurance_no;
          // Two arms because the two list endpoints genuinely differ in shape:
          // AdminEnrollees.php:437-522 mapListRow() nests enrollee fields under
          // `enrollee`, while OrgStaffEnrollees.php:707 spreads them flat. Each
          // is internally consistent, so neither is being changed.
          //
          // This is NOT the same as the three-way hedge at lib.js:952, which
          // guesses at one unambiguous backend. Do not collapse this one, and do
          // not copy it as precedent for guessing.
          const isArchived = Number(row?.enrollee?.archived ?? row?.archived ?? 0) === 1;
          return (
            <Space size={0} onClick={(e) => e.stopPropagation()}>
              {isArchived ? (
                <Tooltip title="Unarchive (restores as inactive)">
                  <Button
                    type="text"
                    size="small"
                    icon={<UndoOutlined />}
                    aria-label="Unarchive enrollee"
                    loading={unarchivingNo === insuranceNo}
                    disabled={!insuranceNo}
                    onClick={() => handleUnarchive(insuranceNo)}
                  />
                </Tooltip>
              ) : null}
              <Tooltip title="View profile">
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  aria-label="View enrollee"
                  onClick={() => openBiodata(row)}
                />
              </Tooltip>
              <Tooltip title="Copy insurance no">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  aria-label="Copy insurance number"
                  disabled={!insuranceNo}
                  onClick={(e) => copyInsuranceNo(insuranceNo, e)}
                />
              </Tooltip>
            </Space>
          );
        },
      },
    ];
  }, [allRegistryColumns, visibleColumnKeys, openBiodata, handleUnarchive, unarchivingNo]);

  const columnPicker = (
    <div className="en-column-picker">
      <div className="en-column-picker__title">Visible columns</div>
      <Checkbox.Group
        value={visibleColumnKeys}
        onChange={(keys) => {
          const next = Object.keys(REGISTRY_COLUMN_META).filter((key) => keys.includes(key));
          if (next.length) setVisibleColumnKeys(next);
        }}
        className="en-column-picker__list"
      >
        {Object.entries(REGISTRY_COLUMN_META).map(([key, meta]) => (
          <Checkbox key={key} value={key}>
            {meta.label}
          </Checkbox>
        ))}
      </Checkbox.Group>
      <Button
        type="link"
        size="small"
        onClick={() => setVisibleColumnKeys(DEFAULT_VISIBLE_COLUMN_KEYS)}
      >
        Reset to default
      </Button>
    </div>
  );

  const registryToolbar = (
    <div className="en-registry-toolbar">
      <span className="en-registry-toolbar__count">
        {pagination.total
          ? `Showing ${listData.length.toLocaleString()} of ${Number(pagination.total).toLocaleString()}`
          : listLoading
            ? 'Loading registry…'
            : 'No results'}
      </span>
      <Space wrap size={8}>
        <Select
          size="small"
          value={tableSize}
          onChange={setTableSize}
          style={{ width: 108 }}
          options={[
            { label: 'Compact', value: 'small' },
            { label: 'Comfort', value: 'middle' },
          ]}
        />
        <Dropdown dropdownRender={() => columnPicker} trigger={['click']}>
          <Button size="small" icon={<SettingOutlined />}>
            Columns
          </Button>
        </Dropdown>
        <Button size="small" icon={<DownloadOutlined />} loading={exporting} onClick={onExport}>
          Export
        </Button>
      </Space>
    </div>
  );

  const activeFilterTags = Object.entries(apiFilters).map(([key, value]) => {
    const meta = FILTER_META[key];
    const label = meta?.label || key;
    const text = key === 'org_id' && selectedOrgLabel
      ? `${selectedOrgLabel} (#${value})`
      : (meta?.format ? meta.format(value) : String(value));
    return { key, label: `${label}: ${text}` };
  });

  const hasSummary = Object.keys(summaryData).length > 0;
  const initialSummaryLoading = summaryLoading && !hasSummary;
  const lastUpdated = lastRefresh
    ? lastRefresh.format('HH:mm')
    : '—';

  const heroMetrics = useMemo(() => {
    if (!hasSummary && !initialSummaryLoading) return [];
    if (initialSummaryLoading) return [];
    const underFive = findUnderFiveCount(summaryData);
    const fivePlus = findFiveAndAboveCount(summaryData);
    return [
      {
        key: 'total',
        title: 'Total enrollees',
        value: Number(summaryData?.total || 0),
        color: '#3497D3',
        icon: <TeamOutlined />,
        subtitle: 'Matching filters',
      },
      {
        key: 'male',
        title: 'Male',
        value: findGroupCount(summaryData?.by_gender, (key) => key === 'male'),
        color: '#3497D3',
        icon: <ManOutlined />,
        subtitle: 'Enrollees',
      },
      {
        key: 'female',
        title: 'Female',
        value: findGroupCount(summaryData?.by_gender, (key) => key === 'female'),
        color: '#722ed1',
        icon: <WomanOutlined />,
        subtitle: 'Enrollees',
      },
      {
        key: 'under-five',
        title: 'Under 5',
        value: underFive,
        color: '#49B870',
        icon: <SmileOutlined />,
        subtitle: `${fivePlus.toLocaleString()} aged 5+`,
      },
    ].filter((m) => hasMetricValue(m.value));
  }, [hasSummary, initialSummaryLoading, summaryData]);

  const hasActiveFilters = activeFilterTags.length > 0;

  return (
    <div className="enrollee-dashboard">
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <h1 className="dash-masthead__title">Enrollees</h1>
            <p className="dash-masthead__meta">
              <span>Updated {lastUpdated}</span>
              {pagination.total ? (
                <>
                  <span className="dash-masthead__sep">·</span>
                  <span>{Number(pagination.total).toLocaleString()} in registry</span>
                </>
              ) : null}
            </p>
          </div>
          <div className="dash-masthead__actions">
            <button
              type="button"
              className={`dash-icon-btn ${showFilters ? 'is-active' : ''}`}
              onClick={() => setShowFilters((v) => !v)}
              aria-label="Toggle filters"
            >
              <FilterOutlined />
              {hasActiveFilters ? <span className="dash-icon-btn__dot" /> : null}
            </button>
            {hasActiveFilters ? (
              <button type="button" className="dash-icon-btn" onClick={clearFilters} aria-label="Clear filters">
                <ClearOutlined />
              </button>
            ) : null}
            <Button
              type="default"
              size="small"
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={onExport}
            >
              Export CSV
            </Button>
            <Button
              type="primary"
              size="small"
              icon={<ReloadOutlined />}
              onClick={loadDashboardData}
              disabled={summaryLoading && listLoading}
              className="dash-masthead__refresh"
            >
              Refresh
            </Button>
          </div>
        </header>
        <ModuleNav items={MODULE_NAVS.enrollees} />

        {showFilters ? (
          <div className="dash-filters">
            <span className="en-filter-section-title">Identity</span>
            <Row gutter={[10, 10]} align="bottom">
              <Col xs={24} md={8} lg={6}>
                <span className="en-filter-label">Search</span>
                <Input
                  placeholder="Name, insurance no, policy no"
                  value={filters.search}
                  onChange={(e) => setFilter('search', e.target.value)}
                  allowClear
                  aria-label="Search enrollees"
                />
                <span className="en-filter-hint">Try insurance no, policy number, or full name</span>
              </Col>
              <Col xs={12} md={4} lg={3}>
                <span className="en-filter-label">Archived</span>
                <Select
                  value={filters.archived || '0'}
                  onChange={(value) => setFilter('archived', value)}
                  options={ARCHIVED_OPTIONS}
                  style={{ width: '100%' }}
                  aria-label="Archived filter"
                />
                <span className="en-filter-hint">Archived enrollees are hidden by default</span>
              </Col>
              <Col xs={12} md={4} lg={3}>
                <span className="en-filter-label">Gender</span>
                <Select
                  placeholder="All"
                  value={filters.gender || undefined}
                  onChange={(value) => setFilter('gender', value || '')}
                  options={GENDER_OPTIONS}
                  allowClear
                  style={{ width: '100%' }}
                  aria-label="Gender filter"
                />
              </Col>
              <Col xs={12} md={4} lg={3}>
                <span className="en-filter-label">Status</span>
                <Select
                  placeholder="All"
                  value={filters.enrollee_status === '' ? undefined : filters.enrollee_status}
                  onChange={(value) => setFilter('enrollee_status', value ?? '')}
                  options={ENROLLEE_STATUS_OPTIONS}
                  allowClear
                  style={{ width: '100%' }}
                  aria-label="Enrollee status filter"
                />
              </Col>
              <Col xs={12} md={4} lg={3}>
                <span className="en-filter-label">Pending update</span>
                <Select
                  placeholder="All"
                  value={filters.update_required === '' ? undefined : filters.update_required}
                  onChange={(value) => setFilter('update_required', value ?? '')}
                  options={BOOL_OPTIONS}
                  allowClear
                  style={{ width: '100%' }}
                  aria-label="Pending update filter"
                />
              </Col>
              <Col xs={12} md={4} lg={3}>
                <span className="en-filter-label">Flagged</span>
                <Select
                  placeholder="All"
                  value={filters.flagged === '' ? undefined : filters.flagged}
                  onChange={(value) => setFilter('flagged', value ?? '')}
                  options={BOOL_OPTIONS}
                  allowClear
                  style={{ width: '100%' }}
                  aria-label="Flagged filter"
                />
              </Col>
            </Row>

            <span className="en-filter-section-title">Demographics</span>
            <Row gutter={[10, 10]} align="bottom">
              <Col xs={12} md={3} lg={2}>
                <span className="en-filter-label">Age min</span>
                <InputNumber
                  placeholder="Min"
                  value={filters.age_min === '' ? null : filters.age_min}
                  onChange={(value) => setFilter('age_min', value ?? '')}
                  style={{ width: '100%' }}
                  min={0}
                  aria-label="Minimum age"
                />
              </Col>
              <Col xs={12} md={3} lg={2}>
                <span className="en-filter-label">Age max</span>
                <InputNumber
                  placeholder="Max"
                  value={filters.age_max === '' ? null : filters.age_max}
                  onChange={(value) => setFilter('age_max', value ?? '')}
                  style={{ width: '100%' }}
                  min={0}
                  aria-label="Maximum age"
                />
              </Col>
              <Col xs={24} md={8} lg={6}>
                <span className="en-filter-label">Date of birth</span>
                <RangePicker
                  style={{ width: '100%' }}
                  value={filters.dob_from && filters.dob_to ? [dayjs(filters.dob_from), dayjs(filters.dob_to)] : null}
                  onChange={(dates) => {
                    if (!dates || !dates[0] || !dates[1]) {
                      setFilter('dob_from', '');
                      setFilter('dob_to', '');
                      return;
                    }
                    setFilter('dob_from', dates[0].format('YYYY-MM-DD'));
                    setFilter('dob_to', dates[1].format('YYYY-MM-DD'));
                  }}
                  aria-label="Date of birth range"
                />
              </Col>
            </Row>

            {canFilterAcrossOrgs ? (
              <>
                <span className="en-filter-section-title">Organization (admin)</span>
                <Row gutter={[10, 10]} align="bottom">
                  <Col xs={24} md={8} lg={5}>
                    <span className="en-filter-label">Organization</span>
                    <DebounceSelect
                      allowClear
                      showSearch
                      placeholder="Search organization"
                      fetchOptions={handleOrgSearch}
                      value={filters.org_id === '' ? undefined : filters.org_id}
                      onChange={(value, option) => {
                        setFilter('org_id', value ?? '');
                        setSelectedOrgLabel(option?.label || '');
                      }}
                      style={{ width: '100%' }}
                      aria-label="Organization filter"
                    />
                  </Col>
                  <Col xs={24} md={6} lg={4}>
                    <span className="en-filter-label">Org code</span>
                    <Input
                      placeholder="Organization code"
                      value={filters.social_no}
                      onChange={(e) => setFilter('social_no', e.target.value)}
                      allowClear
                      aria-label="Organization social number filter"
                    />
                  </Col>
                </Row>
              </>
            ) : null}
          </div>
        ) : null}

        {hasActiveFilters ? (
          <div className="en-active-filters">
            {activeFilterTags.map((item) => (
              <Tag key={item.key}>{item.label}</Tag>
            ))}
          </div>
        ) : null}

        {summaryError ? (
          <Alert type="error" showIcon message={summaryError} />
        ) : null}

        {(hasSummary || initialSummaryLoading) ? (
          <>
            <DashboardHero metrics={heroMetrics} loading={initialSummaryLoading} />

            <DashboardSection
              id="distribution"
              icon={<PieChartOutlined />}
              title="Distribution breakdown"
              subtitle="Counts by dimension for current filters"
              collapsible={false}
              variant="content"
            >
              <div className="en-breakdown-grid">
                {GROUP_KEYS.map((item) => (
                  <BreakdownPanel
                    key={item.key}
                    title={item.title}
                    data={summaryData?.[item.key] || []}
                    loading={initialSummaryLoading}
                  />
                ))}
              </div>
            </DashboardSection>
          </>
        ) : null}

        {!summaryLoading && !hasSummary && !summaryError ? (
          <div className="en-empty-state">
            <Empty description="No enrollee data for the selected filters" />
          </div>
        ) : null}

        <DashboardSection
          id="registry"
          icon={<UnorderedListOutlined />}
          title="Enrollee registry"
          subtitle="Click a row or use actions to open profile"
          collapsible={false}
          variant="content"
        >
          {listError ? (
            <Alert type="error" showIcon message={listError} />
          ) : listLoading && !listData.length ? (
            <TableSkeleton rows={8} columns={6} />
          ) : (
            <>
              {registryToolbar}
              <Table
                className="en-registry-table"
                rowKey={(row, idx) => row?.enrollee?.id || row?.enrollee?.insurance_no || idx}
                columns={registryColumns}
                dataSource={listData}
                size={tableSize}
                loading={listLoading && listData.length > 0}
                scroll={{ x: 'max-content' }}
                pagination={{
                  current: pagination.page,
                  pageSize: pagination.limit,
                  total: pagination.total || 0,
                  showSizeChanger: true,
                  pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
                  showTotal: (total) => `${total.toLocaleString()} enrollee(s)`,
                  onChange: (page, pageSize) => {
                    setPagination((prev) => ({ ...prev, page, limit: pageSize }));
                  },
                }}
                locale={{
                  emptyText: (
                    <Empty
                      description="No enrollees match filters"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                      {hasActiveFilters ? (
                        <Button size="small" onClick={clearFilters}>Clear filters</Button>
                      ) : null}
                    </Empty>
                  ),
                }}
                onRow={(row) => ({
                  onClick: () => openBiodata(row),
                })}
              />
            </>
          )}
        </DashboardSection>
      </div>

      {biodataRow?.enrollee ? (
        <EnroleeBiodataModal
          key={biodataRow.enrollee.insurance_no || biodataRow.enrollee.id}
          data={biodataRow.enrollee}
          policy={biodataRow.policy || null}
          display={showBiodata}
          action={closeBiodata}
          command={loadDashboardData}
          source={1}
          viewOnly={!canEditEnrollee}
        />
      ) : null}
    </div>
  );
};

export default EnrolleeDashboard;
